import { useCallback, useMemo, useState } from "react";
import {
  Event,
  OrganizerType,
  TicketV2,
  AccountData,
  Campaign,
} from "@markit/common.types";
import {
  eventHasRequestTickets,
  isEventExternalLink,
  isEventRelatedType,
  isExternalEventSpecificLink,
  isExternalLinkNoData,
} from "@markit/common.utils";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import { CircularProgress } from "@mui/material";
import EmptyStateButton from "../../components/Buttons/EmptyStateButton";
import ProfileTicketItem, {
  TICKET_FLEX_SECTIONS,
  ORGANIZER_TICKET_FLEX_SECTIONS,
} from "../../components/DisplayItem/ProfileTicketItem";
import { NewItemButton } from "../Buttons/NewItemButton";
import filter from "lodash.filter";
import { EmptyStateFlatlist } from "../EmptyStates/EmptyStateFlatlist";
import EventDashboardLinkAnalyticsBox from "./EventDashboardLinkAnalyticsBox";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { AutomationItemPreview } from "../DisplayItem/CampaignItem/AutomationItemPreview";
import { CampaignDetails } from "../../redux/slices/campaignSlice";
import { DashboardListPanel } from "./DashboardListPanel";
import { SearchBoxContactsMode } from "../Containers/SearchBox/SearchBoxContactsContainer";
import {
  searchByPhoneNumber,
  searchByPhoneNumberNoResults,
} from "../../utils/phoneUtils";
import { EventRefundModal } from "./DashboardModals/EventRefundModal";
import { EventOrganizerPromoterAnalytics } from "./EventOrganizerPromoterAnalytics";
import { LinkedEventPanel } from "./LinkedEventPanel";
import { getAccountState } from "../../redux/slices/accountSlice";
import { useSelector } from "react-redux";
import EnablePaidTicketsModal from "../DisplayModal/EnablePaidTicketsModal";
import { ConnectStripeDashboardBanner } from "./ConnectStripeDashboardBanner";

type GuestListPanelProps = {
  event: Event;
  userTicket: TicketV2 | undefined;
  totalGuests: number;
  totalRequested: number;
  totalOrganizers: number;
  totalRefunded: number;
  totalLinkViews: number;
  promoterTicketsSold: number;
  promoterRevenue: number;
  checkInGuests: boolean;
  canMassText: boolean;
  dashboardVisiblity: number;
  isHost: boolean;
  loading: boolean;
  campaign: Campaign | undefined;
  campaignDetail: CampaignDetails | undefined;
  setIsEventRoleModalOpen: (isEventRoleModalOpen: boolean) => void;
  setRoleOrganizerData: (roleOrganizerData: AccountData | undefined) => void;
  setRoleOrganizerTicketItem: (
    roleOrganizerTicketItem: TicketV2 | undefined
  ) => void;
  setProfilePreviewSelected: (
    profilePreviewSelected: AccountData | undefined
  ) => void;
  isAttendeeLoading: boolean;
  fullAttendeeTicketData: TicketV2[];
  fullAttendeeAccountData: AccountData[];
  loadMoreAttendeeUsers: () => Promise<void>;
  loadAttendeeSearchResultsThrottled: (searchTerm: string) => void;
  loadAttendeeTicketsForUid: (uid: string) => void;
  isPendingRequestsLoading: boolean;
  fullPendingRequestsTicketData: TicketV2[];
  fullPendingRequestsAccountData: AccountData[];
  loadMorePendingRequestsUsers: () => Promise<void>;
  loadPendingRequestsSearchResultsThrottled: (searchTerm: string) => void;
  loadPendingRequestsTicketsForUid: (uid: string) => void;
  isOrganizerLoading: boolean;
  fullOrganizerTicketData: TicketV2[];
  fullOrganizerAccountData: AccountData[];
  loadMoreOrganizerUsers: () => Promise<void>;
  loadOrganizerSearchResultsThrottled: (searchTerm: string) => void;
  loadOrganizerTicketsForUid: (uid: string) => void;
  isRefundedLoading: boolean;
  fullRefundedTicketData: TicketV2[];
  fullRefundedAccountData: AccountData[];
  loadMoreRefundedUsers: () => Promise<void>;
  loadRefundedSearchResultsThrottled: (searchTerm: string) => void;
  loadRefundedTicketsForUid: (uid: string) => void;
  isRefundedFinished: boolean;
  isAttendeeFinished: boolean;
  isPendingRequestsFinished: boolean;
  isOrganizerFinished: boolean;
};

export const GuestListPanel = (props: GuestListPanelProps) => {
  const {
    event,
    userTicket,
    totalGuests,
    totalRequested,
    totalOrganizers,
    totalRefunded,
    totalLinkViews,
    promoterTicketsSold,
    promoterRevenue,
    checkInGuests,
    canMassText,
    dashboardVisiblity,
    isHost,
    loading,
    campaign,
    campaignDetail,
    setIsEventRoleModalOpen,
    setRoleOrganizerData,
    setRoleOrganizerTicketItem,
    setProfilePreviewSelected,
    isAttendeeLoading,
    fullAttendeeTicketData,
    fullAttendeeAccountData,
    loadMoreAttendeeUsers,
    loadAttendeeSearchResultsThrottled,
    loadAttendeeTicketsForUid,
    isPendingRequestsLoading,
    fullPendingRequestsTicketData,
    fullPendingRequestsAccountData,
    loadMorePendingRequestsUsers,
    loadPendingRequestsSearchResultsThrottled,
    loadPendingRequestsTicketsForUid,
    isOrganizerLoading,
    fullOrganizerTicketData,
    fullOrganizerAccountData,
    loadMoreOrganizerUsers,
    loadOrganizerSearchResultsThrottled,
    loadOrganizerTicketsForUid,
    isRefundedLoading,
    fullRefundedTicketData,
    fullRefundedAccountData,
    isRefundedFinished,
    loadMoreRefundedUsers,
    loadRefundedSearchResultsThrottled,
    loadRefundedTicketsForUid,
    isAttendeeFinished,
    isPendingRequestsFinished,
    isOrganizerFinished,
  } = props;
  const { followingAccountData, readyForPayment } =
    useSelector(getAccountState).account;
  const [listValue, setListValue] = useState(0);
  const [stripeVisible, setStripeVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchMode, setSearchMode] = useState<SearchBoxContactsMode>(
    SearchBoxContactsMode.NAME
  );
  const [searchedPhoneUser, setSearchedPhoneUser] = useState<
    AccountData | undefined
  >(undefined);
  const [refundUserItem, setRefundUserItem] = useState<
    | {
        user: AccountData;
        ticket: TicketV2;
      }
    | undefined
  >(undefined);
  const [loadingTicketAction, setLoadingTicketAction] = useState(false);

  const styles = {
    suggestedButtonView: {
      gap: 30,
      marginBottom: canMassText ? 30 : 0,
    },

    sectionContainer: {
      backgroundColor: Colors.WHITE,
      padding: 14,
      borderRadius: 14,
      flex: 1,
    },
  };

  const isPaymentCollectorRole = useMemo(
    () =>
      userTicket?.role.alternatePaymentCollector ||
      userTicket?.role.type === OrganizerType.ALTERNATEPAYMENTCOLLECTOR,
    [userTicket?.role.alternatePaymentCollector, userTicket?.role.type]
  );

  const organizerTab = useMemo(() => listValue === 1, [listValue]);

  const hasRequestTicket = useMemo(
    () => eventHasRequestTickets(event),
    [event]
  );

  const isRequestListView = useMemo(
    () => listValue === 2 && hasRequestTicket,
    [listValue, hasRequestTicket]
  );

  const isRefundedListView = useMemo(
    () => listValue === (hasRequestTicket ? 3 : 2),
    [hasRequestTicket, listValue]
  );

  const isExternalLink = useMemo(
    () => isEventExternalLink(event.eventType),
    [event.eventType]
  );

  const containsUser = useCallback((item: AccountData, query: string) => {
    return item.fullName.toLowerCase().includes(query);
  }, []);

  const currentTicketsToShow = useMemo(() => {
    const ticketsToShow: TicketV2[] =
      listValue === 0
        ? fullAttendeeTicketData
        : listValue === 1
          ? fullOrganizerTicketData
          : isRequestListView
            ? fullPendingRequestsTicketData
            : fullRefundedTicketData;
    const usersToShow: AccountData[] =
      listValue === 0
        ? fullAttendeeAccountData
        : listValue === 1
          ? fullOrganizerAccountData
          : isRequestListView
            ? fullPendingRequestsAccountData
            : fullRefundedAccountData;

    if (searchTerm === "") {
      return ticketsToShow;
    }
    if (searchMode === SearchBoxContactsMode.PHONE) {
      if (!searchedPhoneUser) {
        return [];
      }
      return ticketsToShow.filter((t) => t.uid === searchedPhoneUser.uid);
    }
    let userList: AccountData[] = usersToShow;
    userList = filter(userList, (user: AccountData) => {
      return containsUser(user, searchTerm);
    });
    const tickets: TicketV2[] = [];
    for (let i = 0; i < userList.length; i++) {
      tickets.push(...ticketsToShow.filter((t) => t.uid === userList[i].uid));
    }

    return tickets;
  }, [
    listValue,
    fullAttendeeTicketData,
    fullOrganizerTicketData,
    isRequestListView,
    fullPendingRequestsTicketData,
    fullRefundedTicketData,
    fullAttendeeAccountData,
    fullOrganizerAccountData,
    fullPendingRequestsAccountData,
    fullRefundedAccountData,
    searchTerm,
    searchMode,
    searchedPhoneUser,
    containsUser,
  ]);

  const handleSearch = useCallback(
    async (text: string) => {
      setSearchedPhoneUser(undefined);
      const lowerCaseText = text.toLowerCase();
      setSearchTerm(lowerCaseText);
      if (searchMode === SearchBoxContactsMode.NAME) {
        if (text !== "") {
          if (listValue === 0) {
            loadAttendeeSearchResultsThrottled(lowerCaseText);
          } else if (listValue === 1) {
            loadOrganizerSearchResultsThrottled(lowerCaseText);
          } else if (isRequestListView) {
            loadPendingRequestsSearchResultsThrottled(lowerCaseText);
          } else if (isRefundedListView) {
            loadRefundedSearchResultsThrottled(lowerCaseText);
          }
        }
      } else {
        const searchedPhoneUser = await searchByPhoneNumber(
          text,
          event.createdBy,
          followingAccountData
        );
        if (searchedPhoneUser) {
          setSearchedPhoneUser(searchedPhoneUser.user);
          // Targeted query so this person's ticket shows up even if it hasn't
          // been paginated into the current tab's ticket data yet
          if (listValue === 0) {
            loadAttendeeTicketsForUid(searchedPhoneUser.user.uid);
          } else if (listValue === 1) {
            loadOrganizerTicketsForUid(searchedPhoneUser.user.uid);
          } else if (isRequestListView) {
            loadPendingRequestsTicketsForUid(searchedPhoneUser.user.uid);
          } else if (isRefundedListView) {
            loadRefundedTicketsForUid(searchedPhoneUser.user.uid);
          }
        }
      }
    },
    [
      event.createdBy,
      followingAccountData,
      isRefundedListView,
      isRequestListView,
      listValue,
      loadAttendeeSearchResultsThrottled,
      loadAttendeeTicketsForUid,
      loadOrganizerSearchResultsThrottled,
      loadOrganizerTicketsForUid,
      loadPendingRequestsSearchResultsThrottled,
      loadPendingRequestsTicketsForUid,
      loadRefundedSearchResultsThrottled,
      loadRefundedTicketsForUid,
      searchMode,
    ]
  );

  const allGuestAccountData = useMemo(
    () =>
      fullAttendeeAccountData
        .concat(fullOrganizerAccountData)
        .concat(fullPendingRequestsAccountData)
        .concat(fullRefundedAccountData),
    [
      fullAttendeeAccountData,
      fullOrganizerAccountData,
      fullPendingRequestsAccountData,
      fullRefundedAccountData,
    ]
  );

  const renderTicketItem = useCallback(
    (item: TicketV2) => {
      return (
        <ProfileTicketItem
          ticket={item}
          event={event}
          allGuestAccountData={allGuestAccountData}
          isOrganizer={listValue === 1}
          userTicket={userTicket}
          checkInGuests={checkInGuests}
          setProfilePreviewSelected={setProfilePreviewSelected}
          setIsEventRoleModalOpen={setIsEventRoleModalOpen}
          setRoleOrganizerData={setRoleOrganizerData}
          setRoleOrganizerTicketItem={setRoleOrganizerTicketItem}
          onRefundPress={(user) =>
            setRefundUserItem({ user: user, ticket: item })
          }
          loadingTicketAction={loadingTicketAction}
          setLoadingTicketAction={setLoadingTicketAction}
        />
      );
    },
    [
      allGuestAccountData,
      checkInGuests,
      event,
      listValue,
      loadingTicketAction,
      setIsEventRoleModalOpen,
      setProfilePreviewSelected,
      setRoleOrganizerData,
      setRoleOrganizerTicketItem,
      userTicket,
    ]
  );

  const renderEmptyGuestList = useMemo(() => {
    const isLoading =
      listValue === 0
        ? isAttendeeLoading
        : listValue === 1
          ? isOrganizerLoading
          : isRequestListView
            ? isPendingRequestsLoading
            : isRefundedLoading;
    return (
      <EmptyStateFlatlist
        searchTerm={searchTerm}
        isLoading={isLoading}
        containerStyles={{ paddingTop: 120 }}
        noResultsDescription={
          searchMode === SearchBoxContactsMode.PHONE
            ? searchByPhoneNumberNoResults(searchTerm)
            : "Check for typos, search for something else, or switch to phone search."
        }
        nonSearchEmptyView={
          <EmptyStateButton
            title={
              isEventRelatedType(event.eventType)
                ? isRequestListView
                  ? "No Requests"
                  : isRefundedListView
                    ? "No Refunds"
                    : "No Guests"
                : "No Submissions"
            }
            description={`Your ${
              isEventRelatedType(event.eventType) ? "guests" : "submissions"
            } will show up here`}
            icon={
              <Icon
                icon={"ion:share"}
                height={70}
                style={{ color: Colors.GRAY1 }}
              />
            }
            iconBox={109.5}
            containerStyles={{ paddingTop: 120 }}
          />
        }
      />
    );
  }, [
    event.eventType,
    isAttendeeLoading,
    isOrganizerLoading,
    isPendingRequestsLoading,
    isRefundedListView,
    isRefundedLoading,
    isRequestListView,
    listValue,
    searchMode,
    searchTerm,
  ]);

  const handleLoadMore = useCallback(() => {
    if (searchTerm.trim() === "") {
      if (listValue === 0) {
        loadMoreAttendeeUsers();
      } else if (listValue === 1) {
        loadMoreOrganizerUsers();
      } else if (isRequestListView) {
        loadMorePendingRequestsUsers();
      } else if (isRefundedListView) {
        loadMoreRefundedUsers();
      }
    }
  }, [
    listValue,
    isRequestListView,
    isRefundedListView,
    searchTerm,
    loadMoreAttendeeUsers,
    loadMoreOrganizerUsers,
    loadMorePendingRequestsUsers,
    loadMoreRefundedUsers,
  ]);

  const isListLoading =
    listValue === 0
      ? isAttendeeLoading
      : listValue === 1
        ? isOrganizerLoading
        : isRequestListView
          ? isPendingRequestsLoading
          : isRefundedLoading;

  const isListFinished =
    listValue === 0
      ? isAttendeeFinished
      : listValue === 1
        ? isOrganizerFinished
        : isRequestListView
          ? isPendingRequestsFinished
          : isRefundedFinished;

  const listPanelConfig = useMemo(
    () => ({
      listValue,
      setListValue,
      tabLabels: ["Attendees", "Organizers"]
        .concat(hasRequestTicket ? ["Requested"] : [])
        .concat(totalRefunded > 0 ? ["Refunded"] : []),
      tabNumbers: [
        loading ? -1 : totalGuests,
        loading ? -1 : totalOrganizers,
      ].concat(
        hasRequestTicket ? [loading ? -1 : totalRequested] : [],
        totalRefunded > 0 ? [loading ? -1 : totalRefunded] : []
      ),
      useSubmissionsHeader: isExternalLink,
      submissionsCount: totalGuests,
      searchTerm,
      onSearch: handleSearch,
      searchMode,
      setSearchMode,
      gridFlexSections:
        organizerTab || isExternalLink
          ? ORGANIZER_TICKET_FLEX_SECTIONS
          : TICKET_FLEX_SECTIONS,
      gridSectionTitles: isExternalLink
        ? ["Name", "Submitted"]
        : organizerTab
          ? ["Name", "Role"]
          : ["Name", "Date", "Ticket Type", "Sale"],
    }),
    [
      listValue,
      hasRequestTicket,
      totalRefunded,
      loading,
      totalGuests,
      totalOrganizers,
      totalRequested,
      isExternalLink,
      searchTerm,
      searchMode,
      handleSearch,
      organizerTab,
    ]
  );

  return (
    <>
      <div className="ColumnNormal" style={{ gap: 30 }}>
        {campaign && campaignDetail ? (
          <StandardBorderedContainer containerStyles={styles.sectionContainer}>
            <AutomationItemPreview
              campaignItem={campaign}
              campaignDetail={campaignDetail}
              onEventDashboard
            />
          </StandardBorderedContainer>
        ) : null}
        {userTicket && userTicket.role.type === OrganizerType.PROMOTER ? (
          <EventOrganizerPromoterAnalytics
            userTicket={userTicket}
            promoterTicketsSold={promoterTicketsSold}
            promoterRevenue={promoterRevenue}
            event={event}
          />
        ) : null}
        {userTicket && isPaymentCollectorRole ? (
          readyForPayment ? (
            userTicket.role.type === OrganizerType.ALTERNATEPAYMENTCOLLECTOR ? (
              <StandardBorderedContainer
                containerStyles={styles.sectionContainer}
              >
                <div className="AlignedRowSpaced">
                  <div>
                    <div className="EventDashboardContentHeader">
                      You&apos;re All Set
                    </div>
                    <div className="EventDashboardContentDescription">
                      Contact the event host with questions or to change your
                      role
                    </div>
                  </div>
                </div>
              </StandardBorderedContainer>
            ) : null
          ) : (
            <ConnectStripeDashboardBanner
              title="Connect to Stripe to Start Collecting Payments"
              description="You've been assigned as the payment collector for this event. Connect your Stripe account to start collecting payments."
              onConnectPress={() => setStripeVisible(true)}
            />
          )
        ) : null}
        {isExternalLinkNoData(event.eventType) && totalGuests === 0 ? (
          <EventDashboardLinkAnalyticsBox totalLinkViews={totalLinkViews} />
        ) : null}
        {isExternalEventSpecificLink(event.eventType) ? (
          <LinkedEventPanel event={event} />
        ) : checkInGuests ? (
          <DashboardListPanel
            config={listPanelConfig}
            event={event}
            dashboardVisiblity={dashboardVisiblity}
            tickets={currentTicketsToShow}
            renderItem={renderTicketItem}
            onLoadMore={handleLoadMore}
            emptyState={renderEmptyGuestList}
            listFooter={
              isListLoading && !isListFinished ? (
                <div className="Centering" style={{ paddingBlock: 14 }}>
                  <CircularProgress style={{ color: Colors.GRAY1 }} size={24} />
                </div>
              ) : listValue === 1 && currentTicketsToShow.length > 0 ? (
                <NewItemButton
                  onPress={() => setIsEventRoleModalOpen(true)}
                  label="Add Organizer..."
                  subText={isHost ? "" : "Only available for event owner"}
                  disabled={!isHost}
                />
              ) : (
                <div style={{ height: 40 }} />
              )
            }
            showScanButton={!isExternalLink}
            isLoading={loading}
          />
        ) : null}
      </div>
      {refundUserItem ? (
        <EventRefundModal
          event={event}
          setModalVisible={() => setRefundUserItem(undefined)}
          ticket={refundUserItem.ticket}
          userData={refundUserItem.user}
        />
      ) : null}
      {stripeVisible ? (
        <EnablePaidTicketsModal
          isVisible={stripeVisible}
          setIsVisible={setStripeVisible}
        />
      ) : null}
    </>
  );
};
